import React, {useState} from 'react';
import styled from 'styled-components';
import type {SavedFilter} from '../hooks/useSavedFilters';
import type {DrawerFilterState} from '../hooks/useFilterDrawer';

interface Props {
	filters: SavedFilter[];
	onLoad: (criteria: Partial<DrawerFilterState>) => void;
	onSave: (name: string) => void;
	onDelete: (id: string) => void;
}

const Section = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding-bottom: 12px;
`;

const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
`;

const SectionLabel = styled.span`
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #334155;
`;

const ToggleButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #4f46e5;
	cursor: pointer;
	&:hover {
		color: #4338ca;
	}
`;

const InputRow = styled.div`
	display: flex;
	gap: 8px;
	margin-bottom: 8px;
`;

const NameInput = styled.input`
	flex: 1 1 0%;
	border-radius: 6px;
	border: 1px solid #e2e8f0;
	padding: 6px 10px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #334155;
	&::placeholder {
		color: #94a3b8;
	}
	&:focus {
		outline: none;
		border-color: #a5b4fc;
		box-shadow: 0 0 0 1px #a5b4fc;
	}
`;

const SaveButton = styled.button`
	border: 0;
	border-radius: 6px;
	background: #4f46e5;
	padding: 6px 12px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #4338ca;
	}
	&:disabled {
		opacity: 0.5;
	}
`;

const EmptyText = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;

const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
`;

const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
	padding: 4px 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #475569;
`;

const LoadButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	font: inherit;
	color: inherit;
	cursor: pointer;
	&:hover {
		color: #4338ca;
	}
`;

const DeleteButton = styled.button`
	margin-left: 2px;
	border: 0;
	background: transparent;
	padding: 0;
	color: #94a3b8;
	cursor: pointer;
	&:hover {
		color: #ef4444;
	}
`;

export function FilterDrawerSavedFilters({filters, onLoad, onSave, onDelete}: Props) {
	const [showInput, setShowInput] = useState(false);
	const [name, setName] = useState('');

	function handleSave() {
		const trimmed = name.trim();
		if (!trimmed) return;
		onSave(trimmed);
		setName('');
		setShowInput(false);
	}

	return (
		<Section>
			<Head>
				<SectionLabel>Saved Filters</SectionLabel>
				<ToggleButton type="button" onClick={() => setShowInput(v => !v)}>
					{showInput ? 'Cancel' : '+ Save current'}
				</ToggleButton>
			</Head>

			{showInput && (
				<InputRow>
					<NameInput
						type="text"
						placeholder="Filter name..."
						value={name}
						onChange={e => setName(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && handleSave()}
					/>
					<SaveButton type="button" onClick={handleSave} disabled={!name.trim()}>
						Save
					</SaveButton>
				</InputRow>
			)}

			{filters.length === 0 ? (
				<EmptyText>No saved filters yet</EmptyText>
			) : (
				<Chips>
					{filters.map(f => (
						<Chip key={f.id}>
							<LoadButton type="button" onClick={() => onLoad(f.criteria as Partial<DrawerFilterState>)}>
								{f.name}
							</LoadButton>
							<DeleteButton type="button" onClick={() => onDelete(f.id)} aria-label={`Delete ${f.name}`}>
								&times;
							</DeleteButton>
						</Chip>
					))}
				</Chips>
			)}
		</Section>
	);
}
